import { useEffect, type VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';

import { useAddToast } from '../../../../../../contexts/ToastContext';
import useUpdateTeamGroup from '../../../../../../hooks/api/teams/teamGroups/useUpdateTeamGroup';
import useInputValue from '../../../../../../hooks/useInputValue';
import { getErrorMessage } from '../../../../../../ReactQuery';
import Button, { ButtonGroup } from '../../../../../generic/Button';
import FormGroup from '../../../../../generic/Forms/FormGroup';
import FormLabel from '../../../../../generic/Forms/FormLabel';
import LabeledTextInput from '../../../../../generic/Forms/LabeledTextInput';
import Select from '../../../../../generic/Select/Select';
import useSelectRoles from '../../hooks/useSelectRoles';

type Props = {
  isOpen: boolean;
  onHide: () => void;
  teamGroupId: number;
  teamGroupName: string;
  selectedRoleId: number;
};

/**
 * Modal for editing a team group.
 * Allows the user to change the team group name and default role.
 *
 * @param {Props} props - The props for the component.
 * @returns {React.ReactNode} - The component.
 */
const EditTeamGroupModal: VoidFunctionComponent<Props> = ({
  isOpen,
  onHide,
  teamGroupId,
  teamGroupName,
  selectedRoleId,
}) => {
  const addToast = useAddToast();
  //  Local state for editing the team group name.
  const [teamGroupNameValue, onChangeTeamGroupName] = useInputValue(teamGroupName);
  const { roleOptions, selectedRole, onChangeRole, isLoading: isLoadingRoles } = useSelectRoles({
    selectedRoleId,
    teamId: teamGroupId,
  });

  //  Update the team group name when the props change.
  useEffect(() => {
    if (teamGroupName) {
      onChangeTeamGroupName(teamGroupName);
    }
  }, [teamGroupName, onChangeTeamGroupName]);

  const { mutateAsync: updateTeamGroup, isLoading: isUpdatingTeamGroup } = useUpdateTeamGroup({
    id: teamGroupId,
    name: teamGroupNameValue,
    defaultRoleId: selectedRole?.value ?? selectedRoleId,
  });

  //  Handle the save button click.
  const handleSave = async () => {
    try {
      await updateTeamGroup();
      addToast({
        type: 'success',
        content: `The team group "${teamGroupNameValue}" has been updated successfully.`,
      });
      //  We DON'T clear the state here, since we're always editing just one group at a time.
      onHide();
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  };

  return (
    <Modal show={isOpen} onHide={onHide} animation>
      <Modal.Header closeButton>
        <Modal.Title>
          Edit Team Group
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup>
          <LabeledTextInput
            label="Team Group Name"
            value={teamGroupNameValue}
            onChange={onChangeTeamGroupName}
            size="small"
            placeholder="Enter a name for this team group"
            autoFocus
          />
          <FormLabel label="Default Role">
            <Select
              id="team-default-role-select"
              options={roleOptions}
              value={selectedRole}
              onChange={onChangeRole}
              isDisabled={isLoadingRoles}
            />
          </FormLabel>
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
            isDisabled={isUpdatingTeamGroup}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={handleSave}
            isDisabled={isUpdatingTeamGroup}
          >
            Save
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default EditTeamGroupModal;
